import { eftPath } from "@cle/constants/routes";
import type { AfventendeIndberetningModel } from "@/interfaces/afventendeIndberetning";
import { getAfgorelseDetailPath } from "@/interfaces/afgorelse";
import { formatDanishLongDate } from "@/util/date";
import type { PendingReportingPeriodItem } from "../components/pending-reports/PendingReportCard";

const AFVENTENDE_TYPE = {
  efteruddannelse: 1,
  paabud: 2,
  paalaeg: 3,
} as const;

const normalizeType = (type: AfventendeIndberetningModel["type"]): string => {
  if (typeof type === "number") {
    if (type === AFVENTENDE_TYPE.paabud) {
      return "paabud";
    }

    if (type === AFVENTENDE_TYPE.paalaeg) {
      return "paalaeg";
    }

    if (type === AFVENTENDE_TYPE.efteruddannelse) {
      return "efteruddannelse";
    }
  }

  return String(type).toLowerCase();
};

const isPaabudIndberetning = (item: AfventendeIndberetningModel): boolean => {
  if (item.paabudId) {
    return true;
  }

  const normalizedType = normalizeType(item.type);

  return normalizedType.includes("paabud") || normalizedType.includes("påbud");
};

const isPaalaegIndberetning = (item: AfventendeIndberetningModel): boolean => {
  if (item.paalaegId) {
    return true;
  }

  const normalizedType = normalizeType(item.type);

  return normalizedType.includes("paalaeg") || normalizedType.includes("pålæg");
};

export const mapAfventendeIndberetningToPendingItem = (
  item: AfventendeIndberetningModel,
): PendingReportingPeriodItem => {
  const reportingDeadline = formatDanishLongDate(item.frist);
  // formatDanishLongDate falder tilbage til "Ingen frist" ved manglende dato, hvilket
  // ville give "Ingen frist - 30. september 2027" i et interval. Vis hellere intet.
  const periodRange =
    item.periodeFraDato && item.periodeTilDatoMinusEnDag
      ? `${formatDanishLongDate(
          item.periodeFraDato,
        )} - ${formatDanishLongDate(item.periodeTilDatoMinusEnDag)}`
      : "";

  if (isPaabudIndberetning(item)) {
    const id = item.paabudId ?? item.id;

    return {
      id,
      kind: "injunction",
      title: "Påbud",
      periodRange,
      requiredLessons: item.lektionsKrav,
      transferredLessons: 0,
      reportedLessons: item.lektionerTilRådighed,
      reportedLessonsKnown: item.lektionerTilRaadighedKendt,
      documentationDeadline: reportingDeadline,
      reportingDeadline,
      ctaLabel: "Indberet",
      to: getAfgorelseDetailPath("paabud", id),
    };
  }

  if (isPaalaegIndberetning(item)) {
    const id = item.paalaegId ?? item.id;

    return {
      id,
      kind: "imposition",
      title: "Pålæg",
      periodRange,
      requiredLessons: item.lektionsKrav,
      transferredLessons: 0,
      reportedLessons: item.lektionerTilRådighed,
      reportedLessonsKnown: item.lektionerTilRaadighedKendt,
      documentationDeadline: reportingDeadline,
      reportingDeadline,
      ctaLabel: "Indberet",
      to: getAfgorelseDetailPath("paalaeg", id),
    };
  }

  return {
    id: item.efteruddannelsesperiodeId ?? item.id,
    kind: "reporting-period",
    title: "Efteruddannelsesperiode",
    periodRange,
    requiredLessons: item.lektionsKrav,
    transferredLessons: item.lektionerOverfoertTilDenne.sum,
    reportedLessons: item.lektionerTilRådighed,
    reportedLessonsKnown: item.lektionerTilRaadighedKendt,
    documentationDeadline: reportingDeadline,
    reportingDeadline,
    ctaLabel: "Indberet",
    to: eftPath(`indberetninger/${item.efteruddannelsesperiodeId ?? item.id}`),
  };
};
